/**
 * The app's single emoji catalogue.
 *
 * C3 (2.1.0) cut it for reactions; C3b (2.1.1) made it the catalogue for circle
 * emojis too, so there is exactly one list to curate. It offers a wide,
 * phone-keyboard-sized choice grouped by category, minus anything that reads as
 * an insult. The backend stores the raw emoji string and accepts any short one,
 * so this list can grow without a backend change — and a reaction (or a circle)
 * already using an emoji since removed from the list still renders fine.
 */

/**
 * Never offered in any picker: insulting, vulgar, sexual, violent, death- or
 * drug-related, plus the negative-judgement set banned when C3 was first cut.
 * A unit test asserts none of these appear, so adding a category can never
 * silently reintroduce one.
 */
export const EXCLUDED_EMOJIS = [
  '👎',
  '🖕',
  '💩',
  '🤢',
  '🤮',
  '💔',
  '☠️',
  '💀',
  '🤡',
  '👺',
  '👹',
  '😡',
  '🤬',
  '😠',
  '🐀',
  '🐍',
  '🍑',
  '🍆',
  '💦',
  '👅',
  '🚬',
  '💉',
  '🔫',
  '🔪',
  '💣',
  '🩸',
  '🤏',
  '🤥',
] as const;

/**
 * Ordered picker sections. `id` maps to `emojiPicker.categories.<id>` in the
 * translations.
 */
export const EMOJI_GROUPS = [
  {
    id: 'smileys',
    emojis: [
      '😀',
      '😃',
      '😄',
      '😁',
      '😆',
      '😅',
      '🤣',
      '😂',
      '🙂',
      '🙃',
      '🫠',
      '😉',
      '😊',
      '😇',
      '🥰',
      '😍',
      '🤩',
      '😘',
      '😗',
      '☺️',
      '😚',
      '😙',
      '🥲',
      '😋',
      '😛',
      '😜',
      '🤪',
      '😝',
      '🤗',
      '🤭',
      '🫢',
      '🫣',
      '🤫',
      '🤔',
      '🫡',
      '🤐',
      '🤨',
      '😐',
      '😑',
      '😶',
      '🫥',
      '😏',
      '🙄',
      '😮‍💨',
      '😌',
      '😔',
      '😪',
      '😴',
      '🥱',
      '🤤',
      '😷',
      '🤒',
      '🤕',
      '🤧',
      '🥵',
      '🥶',
      '🥴',
      '😵',
      '😵‍💫',
      '🤯',
      '🤠',
      '🥳',
      '🥸',
      '😎',
      '🤓',
      '🧐',
      '🫤',
      '😲',
      '😳',
      '🥺',
      '🥹',
      '😦',
      '😧',
      '😨',
      '😰',
      '😥',
      '😢',
      '😭',
      '😱',
      '😖',
      '🤦',
      '🤷',
      '🙈',
      '🙉',
      '🙊',
      '😺',
      '😸',
      '😹',
      '😻',
      '😼',
      '🙀',
      // C3b — inherited from the retired curated circle list
      '👨‍👩‍👧‍👦',
      '👪',
    ],
  },
  {
    id: 'gestures',
    emojis: [
      '👋',
      '🤚',
      '🖐️',
      '✋',
      '🖖',
      '🫱',
      '🫲',
      '🫳',
      '🫴',
      '🫸',
      '🫷',
      '👌',
      '🤌',
      '🤞',
      '🫰',
      '🤟',
      '🤘',
      '🤙',
      '👈',
      '👉',
      '👆',
      '👇',
      '☝️',
      '🫵',
      '👍',
      '✊',
      '👊',
      '🤛',
      '🤜',
      '👏',
      '🙌',
      '🫶',
      '👐',
      '🤲',
      '🙏',
      '✍️',
      '💅',
      '💪',
      '🦾',
      '🦿',
      '🦵',
      '🦶',
      '👀',
      '👁️',
      '🧠',
      '🫀',
      '🫁',
      '🦻',
      '👂',
      '👃',
      '🗣️',
      '🫂',
      '🧑‍🎤',
      '👨‍🎤',
      '👩‍🎤',
      '🧑‍🎨',
      '🕺',
      '💃',
      '🕴️',
      '🧑‍🤝‍🧑',
      '👯',
      '🤸',
      '🤾',
      '🏃',
      '🚶',
      '🧘',
      '🤺',
      '🏄',
      '🚴',
      '🏋️',
      '🤹',
      '🦸',
      '🦹',
      '🧚',
      '🧜',
      '🧞',
      '👽',
      '🤖',
      '🎃',
      // C3b — inherited from the retired curated circle list
      '✌️',
      '🤝',
    ],
  },
  {
    id: 'hearts',
    emojis: [
      '❤️',
      '🧡',
      '💛',
      '💚',
      '💙',
      '💜',
      '🤎',
      '🖤',
      '🤍',
      '❤️‍🔥',
      '❤️‍🩹',
      '💖',
      '💗',
      '💓',
      '💞',
      '💕',
      '💘',
      '💌',
      '💟',
      '♥️',
      '💋',
      '👄',
      '🫦',
      '🌹',
      '🥀',
      '💐',
      '😽',
      '💑',
      '💏',
    ],
  },
  {
    id: 'music',
    emojis: [
      '🎵',
      '🎶',
      '🎼',
      '🎧',
      '🎤',
      '🎸',
      '🎹',
      '🥁',
      '🪘',
      '🎷',
      '🎺',
      '🪗',
      '🎻',
      '🪕',
      '📻',
      '📀',
      '💿',
      '📼',
      '🔊',
      '🔉',
      '🔈',
      '🎚️',
      '🎛️',
      '🎙️',
      '🎬',
      '🎞️',
      '🎫',
      '🎟️',
      '🪩',
      '🕹️',
      '🎉',
      '🎊',
      '🎈',
      '🍾',
      '🥂',
      '🍻',
      '🍺',
      '🍷',
      '🍸',
      '🍹',
      '🧉',
      '🍶',
      '🎆',
      '🎇',
      '🪅',
      '🧨',
    ],
  },
  {
    id: 'animals',
    emojis: [
      '🐶',
      '🐱',
      '🐭',
      '🐹',
      '🐰',
      '🦊',
      '🐻',
      '🐼',
      '🐻‍❄️',
      '🐨',
      '🐯',
      '🦁',
      '🐮',
      '🐷',
      '🐸',
      '🐵',
      '🐔',
      '🐧',
      '🐦',
      '🐤',
      '🐣',
      '🦆',
      '🦅',
      '🦉',
      '🦇',
      '🐺',
      '🐗',
      '🐴',
      '🦄',
      '🐝',
      '🦋',
      '🐌',
      '🐞',
      '🐢',
      '🐙',
      '🦑',
      '🦐',
      '🦞',
      '🦀',
      '🐡',
      '🐠',
      '🐟',
      '🐬',
      '🐳',
      '🐋',
      '🦈',
      '🐊',
      '🐅',
      '🐆',
      '🦓',
      '🦍',
      '🦧',
      '🐘',
      '🦛',
      '🦏',
      '🦒',
      '🦘',
      '🐫',
      '🐪',
      '🦙',
      '🐐',
      '🦌',
      '🐕',
      '🐩',
      '🦮',
      '🐈',
      '🐓',
      '🦃',
      '🦚',
      '🦜',
      '🦢',
      '🕊️',
      '🐇',
      '🦔',
      '🐿️',
      '🦫',
      '🦦',
      '🦥',
      '🐁',
      '🐾',
      '🦭',
      '🦩',
      // C3b — inherited from the retired curated circle list
      '🐲',
    ],
  },
  {
    id: 'nature',
    emojis: [
      '🌸',
      '🌼',
      '🌻',
      '🌺',
      '🌷',
      '🪷',
      '🌱',
      '🌿',
      '🍀',
      '🍃',
      '🌳',
      '🌲',
      '🌴',
      '🌵',
      '🎋',
      '🎍',
      '🍁',
      '🍂',
      '🌾',
      '🪴',
      '🍄',
      '🌰',
      '🐚',
      '🪸',
      '🌍',
      '🌎',
      '🌏',
      '🌕',
      '🌖',
      '🌗',
      '🌘',
      '🌑',
      '🌒',
      '🌓',
      '🌔',
      '🌙',
      '🌛',
      '🌜',
      '🌚',
      '🌝',
      '⭐',
      '🌟',
      '💫',
      '✨',
      '☄️',
      '🪐',
      '🌞',
      '☀️',
      '🌤️',
      '⛅',
      '🌥️',
      '☁️',
      '🌦️',
      '🌧️',
      '⛈️',
      '🌩️',
      '🌨️',
      '❄️',
      '☃️',
      '⛄',
      '🌬️',
      '🌪️',
      '🌫️',
      '🌈',
      '⚡',
      '🔥',
      '💧',
      '🌊',
    ],
  },
  {
    id: 'food',
    emojis: [
      '🍏',
      '🍎',
      '🍐',
      '🍊',
      '🍋',
      '🍌',
      '🍉',
      '🍇',
      '🍓',
      '🫐',
      '🍈',
      '🍒',
      '🥭',
      '🍍',
      '🥥',
      '🥝',
      '🍅',
      '🥑',
      '🥦',
      '🥕',
      '🌽',
      '🥔',
      '🍠',
      '🥐',
      '🥖',
      '🍞',
      '🥨',
      '🧀',
      '🥚',
      '🍳',
      '🧈',
      '🥞',
      '🧇',
      '🥓',
      '🍔',
      '🍟',
      '🍕',
      '🌭',
      '🥪',
      '🌮',
      '🌯',
      '🥙',
      '🥗',
      '🥘',
      '🍝',
      '🍜',
      '🍲',
      '🍛',
      '🍣',
      '🍤',
      '🍙',
      '🍚',
      '🍥',
      '🥟',
      '🍧',
      '🍨',
      '🍦',
      '🥧',
      '🧁',
      '🍰',
      '🎂',
      '🍫',
      '🍬',
      '🍭',
      '🍩',
      '🍪',
      '🥜',
      '🍯',
      '☕',
      '🍵',
      '🧋',
      '🥤',
      '🧊',
      '🍽️',
      '🧂',
    ],
  },
  {
    id: 'travel',
    emojis: [
      '🚀',
      '🛸',
      '✈️',
      '🛫',
      '🚁',
      '🛥️',
      '⛵',
      '🚢',
      '🚂',
      '🚊',
      '🚙',
      '🚗',
      '🏎️',
      '🏍️',
      '🛴',
      '🛹',
      '🚲',
      '🗺️',
      '🧭',
      '🏝️',
      '🏖️',
      '🏜️',
      '🏔️',
      '⛰️',
      '🌋',
      '🗻',
      '🏕️',
      '⛺',
      '🌅',
      '🌄',
      '🌇',
      '🌆',
      '🌃',
      '🌌',
      '🌉',
      '🎡',
      '🎢',
      '🎠',
      '🗽',
      '🗼',
      '🏰',
      '🏟️',
      '🎪',
      '🛝',
      '🏠',
      '🚏',
      '🛎️',
      // C3b — inherited from the retired curated circle list
      '🏡',
    ],
  },
  {
    id: 'objects',
    emojis: [
      '💯',
      '💥',
      '💢',
      '💨',
      '💤',
      '🔔',
      '📣',
      '📢',
      '⏰',
      '⌛',
      '💎',
      '👑',
      '🏆',
      '🥇',
      '🥈',
      '🥉',
      '🎖️',
      '🏅',
      '🎯',
      '🎲',
      '🃏',
      '🎰',
      '🧩',
      '🔮',
      '🪄',
      '🎁',
      '🎀',
      '📱',
      '💻',
      '⌨️',
      '🖥️',
      '🔋',
      '🔌',
      '💡',
      '🔍',
      '🔑',
      '🗝️',
      '🧲',
      '⚙️',
      '🔗',
      '📌',
      '📍',
      '✏️',
      '📝',
      '📖',
      '📚',
      '🗞️',
      '💰',
      '💸',
      '🧿',
      '♻️',
      '✅',
      '✔️',
      '❗',
      '❓',
      '💬',
      '💭',
      '🗯️',
      '🆒',
      '🆕',
      '🆙',
      '🔝',
      '⬆️',
      '➡️',
      '🔄',
      '♾️',
      '〰️',
      '🕶️',
      '👕',
      '👖',
      '👟',
      '🧢',
      '🎩',
      '👗',
      '👠',
      '💍',
      '🛍️',
      '🏀',
      '⚽',
      '🏈',
      '⚾',
      '🎾',
      '🏐',
      '🏓',
      '🥊',
      '⛳',
      '🎳',
      '🎱',
      '🥋',
      '🏹',
      '🎿',
      '🛼',
      '⛸️',
      // C3b — inherited from the retired curated circle list
      '💼',
      '📈',
      '🎮',
      '🏉',
      '🏸',
      '⛷️',
      '🏂',
      '🏊',
      '🧗',
    ],
  },
] as const;

/** Flat, deduplicated list in picker order. */
export const ALL_EMOJIS: readonly string[] = EMOJI_GROUPS.flatMap(
  (group) => group.emojis as readonly string[],
);

export type EmojiGroupId = (typeof EMOJI_GROUPS)[number]['id'];
